import React, { useState } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  Combobox,
  ErrorText,
  Flex,
  Heading,
  Input,
  Label,
} from '@visibly/vui';

import {
  isValidDateOfBirth,
  usDateToIso,
  isoToUsDate,
} from '../../prelogin/services';

function QualificationForm(props) {
  const {
    fields,
    enableDateOfBirth,
    enableLocation,
    onSubmitIsLoading,
    onSubmit,
  } = props;

  const { t } = useTranslation();

  const [showDateOfBirthValidationMessages, setShowDateOfBirthValidationMessages] = useState(false);
  const [showRegionIdValidationMessages, setShowRegionIdValidationMessages] = useState(false);

  const [user, setUser] = useState({ dateOfBirth: '', regionId: '', ...props.user });
  const [region, setRegion] = useState({ id: '', label: '' });

  // Native <input type="date"> exchanges ISO (YYYY-MM-DD) with the DOM, but the rest of the
  // app (submit payloads, validation) speaks MM/DD/YYYY. Convert at the component boundary so
  // the wire format stays unchanged.
  const handleDateOfBirthChange = ({ target }) => {
    setUser({ ...user, dateOfBirth: isoToUsDate(target.value) });
    if (showDateOfBirthValidationMessages) {
      setShowDateOfBirthValidationMessages(false);
    }
  };

  const handleRegionIdChange = (selectedRegion) => {
    setUser({ ...user, regionId: selectedRegion.id });
    setRegion(selectedRegion);
    if (showRegionIdValidationMessages) {
      setShowRegionIdValidationMessages(false);
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    // vui's Input does not forward min/max to the DOM, so the "no future date" (max=today)
    // and lower-bound checks are enforced here in JS via isValidDateOfBirth. Each field is
    // only validated when it is actually rendered (enableDateOfBirth / enableLocation).
    const dateOfBirthValidationResult = !enableDateOfBirth || isValidDateOfBirth(user.dateOfBirth);
    const regionIdValidationResult = !enableLocation || user.regionId !== '';

    setShowDateOfBirthValidationMessages(!dateOfBirthValidationResult);
    setShowRegionIdValidationMessages(!regionIdValidationResult);

    if (dateOfBirthValidationResult && regionIdValidationResult) {
      onSubmit(user);
    }
  };

  return (
    <form
      noValidate
      onSubmit={submitHandler}
    >
      <Box marginBottom='$xxl'>
        <Heading variant='h4' textAlign='center'>{t('qualificationForm.heading')}</Heading>
      </Box>

      {enableDateOfBirth &&
        <Box marginBottom='$lg'>
          <Flex variant='column'>
            <Label
              htmlFor="dateOfBirth"
              error={showDateOfBirthValidationMessages}
            >
              {t('qualificationForm.headingDateOfBirth')}
            </Label>
            <Input
              id="dateOfBirth"
              name="dateOfBirth"
              type="date"
              value={usDateToIso(user.dateOfBirth)}
              required
              onChange={handleDateOfBirthChange}
              error={showDateOfBirthValidationMessages}
            />
            {showDateOfBirthValidationMessages && <ErrorText message={t('qualificationForm.fields.dateOfBirth.validationMessage')} />}
          </Flex>
        </Box>
      }

      {enableLocation &&
        <Box marginBottom='$xxl'>
          <Flex variant='column'>
            <Label
              htmlFor="regionId"
              error={showRegionIdValidationMessages}
            >
              {t('qualificationForm.headingRegion')}
            </Label>
            <Combobox
              {...fields.regionId}
              id="regionId"
              name="regionId"
              filterKey="label"
              value={region}
              placeholder={t('qualificationForm.fields.regionId.label')}
              required
              onChange={handleRegionIdChange}
              optionLabelKey="label"
            />
            {showRegionIdValidationMessages && <ErrorText message={t('qualificationForm.fields.regionId.validationMessage')} />}
          </Flex>
        </Box>
      }

      <Button
        type="submit"
        disabled={onSubmitIsLoading}
      >
        {t('qualificationForm.callToAction')}
      </Button>
    </form>
  );
}

QualificationForm.propTypes = {
  user: PropTypes.shape({
    dateOfBirth: PropTypes.string,
    regionId: PropTypes.string,
  }),
  region: PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
  }),

  fields: PropTypes.exact({
    dateOfBirth: PropTypes.exact({
      pattern: PropTypes.string.isRequired,
    }),
    regionId: PropTypes.exact({
      options: PropTypes.arrayOf(PropTypes.exact({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      })),
    }),
  }),

  enableDateOfBirth: PropTypes.bool,
  enableLocation: PropTypes.bool,
  onSubmitIsLoading: PropTypes.bool,
  onSubmit: PropTypes.func,
};

QualificationForm.defaultProps = {
  user: { dateOfBirth: '', regionId: '' },
  region: { id: '', label: '' },
  fields: {},
  enableDateOfBirth: true,
  enableLocation: true,
  onSubmitIsLoading: false,
  onSubmit: () => {},
};

export default QualificationForm;
